import { zodResolver } from '@hookform/resolvers/zod'
import { ChevronRight, Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { supportService } from '../api/services/support.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { useAuth } from '../context/AuthContext'
import { useSupportRealtime } from '../hooks/useSupportRealtime'
import { extractList } from '../lib/api-list'
import { cn } from '../lib/utils'
import type { SupportRequest } from '../types/support'

/** Load enough tickets for the scrollable sidebar (no UI pagination). */
const TICKET_LIST_LIMIT = 150

const createTicketSchema = z.object({
  subject: z.string().min(1, 'Subject is required'),
  body: z.string().min(1, 'Message is required'),
})

const replySchema = z.object({
  body: z.string().min(1, 'Reply is required'),
})

type CreateTicketFormValues = z.infer<typeof createTicketSchema>
type ReplyFormValues = z.infer<typeof replySchema>

function statusBadgeClass(status: SupportRequest['status']) {
  if (status === 'closed') {
    return 'bg-parch-100 text-ink-600'
  }

  if (status === 'answered') {
    return 'bg-green-50 text-green-800'
  }

  return 'bg-warn-lt text-warn'
}

function SupportTicketDetail({
  ticket,
  closing,
  replying,
  canClose,
  onClose,
  onReply,
}: {
  ticket: SupportRequest
  closing: boolean
  replying: boolean
  canClose: boolean
  onClose: () => void
  onReply: (body: string) => Promise<void>
}) {
  const replyForm = useForm<ReplyFormValues>({
    resolver: zodResolver(replySchema),
    defaultValues: { body: '' },
  })
  const conversationRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const node = conversationRef.current
    if (!node) {
      return
    }

    node.scrollTop = node.scrollHeight
  }, [ticket.id, ticket.replies?.length])

  const submitReply = replyForm.handleSubmit(async (values) => {
    await onReply(values.body)
    replyForm.reset({ body: '' })
  })

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-lg font-bold text-ink-900">{ticket.subject}</h2>
              <span
                className={cn(
                  'shrink-0 rounded-full px-2.5 py-1 text-xs font-medium capitalize',
                  statusBadgeClass(ticket.status),
                )}
              >
                {ticket.status ?? 'open'}
              </span>
            </div>
            <p className="mt-1 text-sm text-ink-500">
              {ticket.created_at ? new Date(ticket.created_at).toLocaleString() : '—'}
            </p>
          </div>
          {canClose && ticket.status !== 'closed' && (
            <Button type="button" size="sm" variant="secondary" disabled={closing} onClick={onClose}>
              {closing ? 'Closing…' : 'Close Ticket'}
            </Button>
          )}
        </div>

        <div className="rounded-lg border border-parch-100 bg-parch-50/60 p-4">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-ink-400">
            Your message
          </p>
          <p className="whitespace-pre-wrap text-sm text-ink-800">{ticket.body}</p>
        </div>

        <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">
          Conversation
          {ticket.replies && ticket.replies.length > 0 ? ` (${ticket.replies.length})` : ''}
        </p>
      </div>

      <div ref={conversationRef} className="mt-3 min-h-0 flex-1 overflow-y-auto pr-1">
        {ticket.replies && ticket.replies.length > 0 ? (
          <div className="space-y-3 pb-2">
            {ticket.replies.map((reply) => (
              <div
                key={reply.id}
                className="rounded-lg border border-parch-100 bg-white p-3 text-sm shadow-sm"
              >
                <p className="mb-1 text-xs font-medium text-ink-500">
                  {reply.user?.name ?? 'Support'} ·{' '}
                  {reply.created_at ? new Date(reply.created_at).toLocaleString() : '—'}
                </p>
                <p className="whitespace-pre-wrap text-ink-800">{reply.body}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-ink-500">No replies yet.</p>
        )}
      </div>

      {ticket.status !== 'closed' && (
        <form
          className="mt-4 shrink-0 space-y-3 border-t border-parch-100 pt-4"
          onSubmit={submitReply}
        >
          <FormRequiredLegend />
          <div>
            <FieldLabel required>Your reply</FieldLabel>
            <textarea
              className="min-h-24 w-full rounded-md border border-parch-200 px-3 py-2 text-sm"
              {...replyForm.register('body')}
            />
            {replyForm.formState.errors.body && (
              <p className="mt-1 text-xs text-danger">{replyForm.formState.errors.body.message}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit" size="sm" disabled={replying}>
              {replying ? 'Sending…' : 'Send Reply'}
            </Button>
          </div>
        </form>
      )}
    </div>
  )
}

export function SupportPage() {
  const { can } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const ticketFromQuery = searchParams.get('ticket')
  const [tickets, setTickets] = useState<SupportRequest[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [subjectSearch, setSubjectSearch] = useState('')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [selectedTicket, setSelectedTicket] = useState<SupportRequest | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [closingId, setClosingId] = useState<string | null>(null)
  const [replyingId, setReplyingId] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const form = useForm<CreateTicketFormValues>({
    resolver: zodResolver(createTicketSchema),
    defaultValues: { subject: '', body: '' },
  })

  const filteredTickets = useMemo(() => {
    const query = subjectSearch.trim().toLowerCase()
    if (!query) {
      return tickets
    }

    return tickets.filter((ticket) => ticket.subject.toLowerCase().includes(query))
  }, [subjectSearch, tickets])

  const loadTickets = useCallback(async () => {
    try {
      const response = await supportService.list({ page: 1, per_page: TICKET_LIST_LIMIT })
      const { items } = extractList(response)
      setTickets(items)
    } catch {
      setTickets([])
    } finally {
      setHasLoaded(true)
    }
  }, [])

  const loadDetail = useCallback(async (id: string) => {
    setLoadingDetail(true)

    try {
      const response = await supportService.show(id)
      setSelectedTicket(response.data)
    } catch {
      setSelectedTicket(null)
      setSelectedId(null)
    } finally {
      setLoadingDetail(false)
    }
  }, [])

  const upsertTicketInList = useCallback((ticket: SupportRequest) => {
    setTickets((current) => {
      const listItem: SupportRequest = {
        id: ticket.id,
        user_id: ticket.user_id,
        subject: ticket.subject,
        body: ticket.body,
        status: ticket.status,
        user: ticket.user,
        created_at: ticket.created_at,
        updated_at: ticket.updated_at,
      }
      const exists = current.some((item) => item.id === ticket.id)

      if (!exists) {
        return [listItem, ...current]
      }

      return current.map((item) => (item.id === ticket.id ? { ...item, ...listItem } : item))
    })
  }, [])

  const refreshTicketFromRealtime = useCallback(
    async (supportRequestId: string) => {
      try {
        const response = await supportService.show(supportRequestId)
        const ticket = response.data
        upsertTicketInList(ticket)

        setSelectedId((currentId) => {
          if (currentId === supportRequestId) {
            setSelectedTicket(ticket)
          }

          return currentId
        })
      } catch {
        // Bell/toast already surfaced the event.
      }
    },
    [upsertTicketInList],
  )

  useSupportRealtime({
    onTicketCreated: (supportRequestId) => {
      void refreshTicketFromRealtime(supportRequestId)
    },
    onTicketReply: (supportRequestId) => {
      void refreshTicketFromRealtime(supportRequestId)
    },
  })

  useEffect(() => {
    void loadTickets()
  }, [loadTickets])

  useEffect(() => {
    if (!ticketFromQuery || !hasLoaded) {
      return
    }

    setSelectedId(ticketFromQuery)

    setSearchParams(
      (prev) => {
        if (!prev.has('ticket')) {
          return prev
        }

        const next = new URLSearchParams(prev)
        next.delete('ticket')
        return next
      },
      { replace: true },
    )
  }, [hasLoaded, setSearchParams, ticketFromQuery])

  useEffect(() => {
    if (!selectedId) {
      setSelectedTicket(null)
      return
    }

    void loadDetail(selectedId)
  }, [loadDetail, selectedId])

  const closeForm = () => {
    setFormOpen(false)
    form.reset({ subject: '', body: '' })
  }

  const onCreate = async (values: CreateTicketFormValues) => {
    setSubmitting(true)

    try {
      const response = await supportService.create(values)
      closeForm()
      setSubjectSearch('')
      await loadTickets()
      if (response.data?.id) {
        setSelectedId(response.data.id)
      }
    } catch {
      // handled globally
    } finally {
      setSubmitting(false)
    }
  }

  const handleCloseTicket = async (id: string) => {
    setClosingId(id)

    try {
      await supportService.close(id)
      await loadTickets()
      if (selectedId === id) {
        await loadDetail(id)
      }
    } catch {
      // handled globally
    } finally {
      setClosingId(null)
    }
  }

  const handleReply = async (id: string, body: string) => {
    setReplyingId(id)

    try {
      await supportService.reply(id, { body })
      await loadTickets()
      await loadDetail(id)
    } catch {
      // handled globally
    } finally {
      setReplyingId(null)
    }
  }

  return (
    <AppShell>
      {/*
        Fill the AppShell content area (viewport − fixed header h-14 − main p-6)
        so cards scroll internally and the page itself does not.
      */}
      <div className="flex h-[calc(100vh-3.5rem-3rem)] min-h-0 flex-col overflow-hidden">
        <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">Support</h1>
            <p className="mt-1 text-sm text-ink-600">
              Open a ticket and chat with the PeerMed team until it is resolved.
            </p>
          </div>

          <PermissionGate permission="support-requests-create">
            <Button onClick={() => setFormOpen(true)}>New Ticket</Button>
          </PermissionGate>
        </div>

        <div className="grid min-h-0 flex-1 gap-6 lg:grid-cols-5">
          <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-parch-100 bg-white shadow-card lg:col-span-2">
            <div className="shrink-0 border-b border-parch-100 bg-parch-50 px-4 py-3">
              <p className="text-sm font-medium text-ink-700">
                Your tickets
                {hasLoaded ? (
                  <span className="ml-1.5 text-ink-400">({filteredTickets.length})</span>
                ) : null}
              </p>
            </div>

            <div className="shrink-0 border-b border-parch-100 px-3 py-3">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                <Input
                  className="pl-9"
                  value={subjectSearch}
                  onChange={(event) => setSubjectSearch(event.target.value)}
                  placeholder="Search by subject…"
                  aria-label="Search tickets by subject"
                />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {!hasLoaded ? (
                <p className="px-4 py-8 text-center text-sm text-ink-500">Loading…</p>
              ) : tickets.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-ink-500">
                  No support tickets yet. Create one to get help from the HRS team.
                </p>
              ) : filteredTickets.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-ink-500">
                  No tickets match “{subjectSearch.trim()}”.
                </p>
              ) : (
                <ul className="divide-y divide-parch-100">
                  {filteredTickets.map((ticket) => {
                    const isSelected = selectedId === ticket.id

                    return (
                      <li key={ticket.id}>
                        <button
                          type="button"
                          className={cn(
                            'flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors',
                            isSelected
                              ? 'border-l-4 border-l-green-600 bg-green-50/70'
                              : 'border-l-4 border-l-transparent hover:bg-parch-50/70',
                          )}
                          onClick={() => setSelectedId(ticket.id)}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <p
                                className={cn(
                                  'truncate text-sm text-ink-900',
                                  isSelected ? 'font-semibold' : 'font-medium',
                                )}
                              >
                                {ticket.subject}
                              </p>
                              <span
                                className={cn(
                                  'shrink-0 rounded-full px-2 py-0.5 text-xs font-medium capitalize',
                                  statusBadgeClass(ticket.status),
                                )}
                              >
                                {ticket.status ?? 'open'}
                              </span>
                            </div>
                            <p className="mt-1 line-clamp-2 text-xs text-ink-500">{ticket.body}</p>
                            <p className="mt-1.5 text-xs text-ink-400">
                              {ticket.created_at
                                ? new Date(ticket.created_at).toLocaleString()
                                : '—'}
                            </p>
                          </div>
                          <ChevronRight
                            className={cn(
                              'mt-1 h-4 w-4 shrink-0 text-ink-300',
                              isSelected && 'text-green-700',
                            )}
                          />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </section>

          <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-parch-100 bg-white p-5 shadow-card sm:p-6 lg:col-span-3">
            {!selectedId ? (
              <div className="flex min-h-48 flex-1 flex-col items-center justify-center text-center">
                <p className="text-sm font-medium text-ink-700">Select a ticket</p>
                <p className="mt-1 max-w-xs text-sm text-ink-500">
                  Choose a ticket from the list to read the conversation and reply.
                </p>
              </div>
            ) : loadingDetail ? (
              <p className="text-sm text-ink-500">Loading…</p>
            ) : selectedTicket ? (
              <SupportTicketDetail
                ticket={selectedTicket}
                closing={closingId === selectedTicket.id}
                replying={replyingId === selectedTicket.id}
                canClose={can('support-requests-close')}
                onClose={() => void handleCloseTicket(selectedTicket.id)}
                onReply={(body) => handleReply(selectedTicket.id, body)}
              />
            ) : (
              <p className="text-sm text-ink-500">Ticket not found.</p>
            )}
          </section>
        </div>

        {formOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-card">
              <h2 className="font-display text-xl font-bold">New support ticket</h2>

              <form className="mt-6 space-y-4" onSubmit={form.handleSubmit(onCreate)}>
                <FormRequiredLegend />
                <div>
                  <FieldLabel required>Subject</FieldLabel>
                  <Input {...form.register('subject')} />
                  {form.formState.errors.subject && (
                    <p className="mt-1 text-xs text-danger">
                      {form.formState.errors.subject.message}
                    </p>
                  )}
                </div>

                <div>
                  <FieldLabel required>Message</FieldLabel>
                  <textarea
                    className="min-h-32 w-full rounded-md border border-parch-200 px-3 py-2 text-sm"
                    {...form.register('body')}
                  />
                  {form.formState.errors.body && (
                    <p className="mt-1 text-xs text-danger">{form.formState.errors.body.message}</p>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={closeForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Submitting…' : 'Submit Ticket'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
